if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        // Cleanup variant change listener
        if (this.variantChangeUnsubscribe) {
          this.variantChangeUnsubscribe();
          this.variantChangeUnsubscribe = null;
        }

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Reinitialize media gallery for quick add modal
            this.reinitializeMediaGallery();

            // Add variant change listener for quick add modal
            this.addVariantChangeListener();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      reinitializeMediaGallery() {
        // Find the media gallery in the quick add modal
        const mediaGallery = this.modalContent.querySelector('media-gallery');
        if (!mediaGallery) return;

        // Wait a bit for the DOM to be ready
        setTimeout(() => {
          // Check if media gallery is already initialized
          if (!mediaGallery.elements) {
            // Manually initialize the media gallery
            mediaGallery.elements = {
              liveRegion: mediaGallery.querySelector('[id^="GalleryStatus"]'),
              viewer: mediaGallery.querySelector('[id^="GalleryViewer"]'),
              thumbnails: mediaGallery.querySelector('[id^="GalleryThumbnails"]'),
            };
            mediaGallery.mql = window.matchMedia('(min-width: 750px)');
            
            // Bind thumbnail click events using the original media gallery logic
            if (mediaGallery.elements.thumbnails) {
              const thumbnails = mediaGallery.elements.thumbnails.querySelectorAll('[data-target]');
              thumbnails.forEach((mediaToSwitch) => {
                const button = mediaToSwitch.querySelector('button');
                if (button) {
                  button.addEventListener('click', () => {
                    const mediaId = mediaToSwitch.dataset.target;
                    console.log('Thumbnail clicked, mediaId:', mediaId);
                    this.setActiveMediaInQuickAdd(mediaId, false);
                  });
                }
              });
            }
          }
        }, 100);
      }

      setActiveMediaInQuickAdd(mediaId, prepend) {
        const mediaGallery = this.modalContent.querySelector('media-gallery');
        if (!mediaGallery) {
          console.log('Media gallery not found in quick add modal');
          return;
        }

        // Use the initialized elements if available, otherwise query fresh
        const viewer = mediaGallery.elements?.viewer || mediaGallery.querySelector('[id^="GalleryViewer"]');
        const thumbnails = mediaGallery.elements?.thumbnails || mediaGallery.querySelector('[id^="GalleryThumbnails"]');
        
        if (!viewer) {
          console.error('Gallery viewer not found in quick add modal');
          return;
        }

        console.log('setActiveMediaInQuickAdd called with mediaId:', mediaId); // Debug log

        // Find the active media in the viewer
        let activeMedia = viewer.querySelector(`[data-media-id="${mediaId}"]`);
        
        // If exact match not found, try to find by partial match (in case ID format differs)
        if (!activeMedia) {
          const mediaIdParts = mediaId.split('-');
          const lastPart = mediaIdParts[mediaIdParts.length - 1]; // Get the actual media ID
          console.log('Trying partial match with media ID:', lastPart); // Debug log
          
          activeMedia = viewer.querySelector(`[data-media-id*="${lastPart}"]`);
          if (!activeMedia) {
            // Try even broader match
            const allMedia = viewer.querySelectorAll('[data-media-id]');
            console.log('Available media IDs:', Array.from(allMedia).map(el => el.dataset.mediaId)); // Debug log
            
            // Fallback: try to find any media that contains the quickadd prefix
            activeMedia = viewer.querySelector('[data-media-id^="quickadd-"]');
          }
        }

        if (!activeMedia) {
          console.error('No media element found for mediaId:', mediaId);
          return;
        }

        console.log('Found active media:', activeMedia.dataset.mediaId); // Debug log

        // Remove active class from all media items and add to the current one
        viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        // Update thumbnail active state
        if (thumbnails) {
          const activeThumbnail = thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (!activeThumbnail) {
            // Try partial match for thumbnails too
            const mediaIdParts = mediaId.split('-');
            const lastPart = mediaIdParts[mediaIdParts.length - 1];
            const allThumbnails = thumbnails.querySelectorAll('[data-target]');
            
            for (const thumbnail of allThumbnails) {
              if (thumbnail.dataset.target.includes(lastPart)) {
                thumbnail.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
                thumbnail.querySelector('button').setAttribute('aria-current', true);
                break;
              }
            }
          } else {
            thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
            activeThumbnail.querySelector('button').setAttribute('aria-current', true);
          }
        }

        // Scroll to the active media if needed
        if (prepend && activeMedia.parentElement) {
          const mediaContainer = activeMedia.parentElement;
          mediaContainer.firstChild !== activeMedia && mediaContainer.prepend(activeMedia);
        }

        console.log('Successfully set active media:', mediaId); // Debug log
      }

      addVariantChangeListener() {
        // Find the variant-selects element in the quick add modal
        const variantSelects = this.modalContent.querySelector('variant-selects');
        if (!variantSelects) {
          console.log('No variant-selects found in quick add modal');
          return;
        }

        console.log('Setting up variant change listeners in quick add modal'); // Debug log

        // Wait a bit for the variant-selects to be fully initialized
        setTimeout(() => {
          // Subscribe to variant change events like the main product page does
          if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.optionValueSelectionChange) {
            const unsubscribe = subscribe(
              window.PUB_SUB_EVENTS.optionValueSelectionChange,
              (event) => {
                // Only process if the event is from our quick add modal
                if (this.modalContent.contains(event.data.target)) {
                  console.log('Variant changed in quick add modal via pubsub', event.data); // Debug log
                  this.handleVariantChange(event);
                }
              }
            );
            
            // Store unsubscribe function for cleanup
            this.variantChangeUnsubscribe = unsubscribe;
            console.log('Pubsub variant listener set up'); // Debug log
          } else {
            console.log('PUB_SUB_EVENTS not available, using fallback'); // Debug log
          }

          // Fallback: Listen for change events on variant inputs
          const variantInputs = variantSelects.querySelectorAll('input[type="radio"], select');
          console.log('Found variant inputs:', variantInputs.length); // Debug log
          
          variantInputs.forEach((input, index) => {
            input.addEventListener('change', (e) => {
              console.log('Variant input changed in quick add modal', e.target.value, index); // Debug log
              this.handleVariantChangeFromInput(e);
            });
          });

          // Also listen for click events on variant labels (for radio buttons)
          const variantLabels = variantSelects.querySelectorAll('label');
          variantLabels.forEach((label) => {
            label.addEventListener('click', (e) => {
              setTimeout(() => {
                const input = label.querySelector('input[type="radio"]');
                if (input && input.checked) {
                  console.log('Variant label clicked and checked', input.value); // Debug log
                  this.handleVariantChangeFromInput({ target: input });
                }
              }, 10);
            });
          });
        }, 200);
      }

      handleVariantChange(event) {
        const { target, selectedOptionValues } = event.data;
        const productInfo = this.modalContent.querySelector('product-info');
        
        if (!productInfo) return;

        // Get the selected variant data
        const selectedVariant = this.getSelectedVariantFromOptions(selectedOptionValues, productInfo);
        if (selectedVariant && selectedVariant.featured_media) {
          const sectionId = productInfo.dataset.section || productInfo.dataset.originalSection;
          const mediaId = `quickadd-${sectionId}-${selectedVariant.featured_media.id}`;
          console.log('Setting media for variant change:', mediaId); // Debug log
          this.setActiveMediaInQuickAdd(mediaId, false);
        }
      }

      handleVariantChangeFromInput(event) {
        const productInfo = this.modalContent.querySelector('product-info');
        if (!productInfo) return;

        // Get all selected option values
        const variantSelects = this.modalContent.querySelector('variant-selects');
        const selectedInputs = variantSelects.querySelectorAll('input:checked, select option:selected');
        const selectedOptionValues = Array.from(selectedInputs).map(input => input.value);

        // Find the variant that matches these options
        const selectedVariant = this.getSelectedVariantFromOptions(selectedOptionValues, productInfo);
        if (selectedVariant && selectedVariant.featured_media) {
          const sectionId = productInfo.dataset.section || productInfo.dataset.originalSection;
          const mediaId = `quickadd-${sectionId}-${selectedVariant.featured_media.id}`;
          console.log('Setting media for input change:', mediaId); // Debug log
          this.setActiveMediaInQuickAdd(mediaId, false);
        }
      }

      getSelectedVariantFromOptions(selectedOptionValues, productInfo) {
        console.log('Getting variant for options:', selectedOptionValues); // Debug log

        // Method 1: Try to get variant data from the product-info element
        const selectedVariantElement = productInfo.querySelector('[data-selected-variant]');
        if (selectedVariantElement) {
          try {
            const variant = JSON.parse(selectedVariantElement.textContent);
            console.log('Found selected variant from element:', variant); // Debug log
            return variant;
          } catch (e) {
            console.log('Error parsing selected variant:', e);
          }
        }

        // Method 2: Try to find variant data in script tags
        const variantScripts = productInfo.querySelectorAll('script[type="application/json"]');
        console.log('Found variant scripts:', variantScripts.length); // Debug log
        
        for (const variantScript of variantScripts) {
          try {
            const data = JSON.parse(variantScript.textContent);
            const variants = data.variants || data;
            console.log('Checking variants array:', variants.length, 'variants'); // Debug log
            
            // Find variant that matches the selected options
            const matchingVariant = variants.find(v => {
              if (!v.options) return false;
              const match = v.options.every(option => selectedOptionValues.includes(option));
              if (match) {
                console.log('Found matching variant:', v); // Debug log
              }
              return match;
            });
            
            if (matchingVariant) {
              return matchingVariant;
            }
          } catch (e) {
            console.log('Error parsing variant script:', e);
          }
        }

        // Method 3: Try to get all product variants and match
        const allScripts = document.querySelectorAll('script[type="application/json"]');
        for (const script of allScripts) {
          try {
            const data = JSON.parse(script.textContent);
            if (data.variants && Array.isArray(data.variants)) {
              const matchingVariant = data.variants.find(v => {
                if (!v.options) return false;
                return v.options.every(option => selectedOptionValues.includes(option));
              });
              
              if (matchingVariant) {
                console.log('Found matching variant from global scripts:', matchingVariant); // Debug log
                return matchingVariant;
              }
            }
          } catch (e) {
            // Skip scripts that can't be parsed
          }
        }

        console.log('No matching variant found for options:', selectedOptionValues); // Debug log
        return null;
      }

      getVariantData(variantId) {
        // Try to find variant data in the quick add modal
        const productInfo = this.modalContent.querySelector('product-info');
        if (!productInfo) return null;

        // Method 1: Look for script tag with variant data (most common Shopify pattern)
        const variantScript = productInfo.querySelector('script[type="application/json"][data-variants]') ||
                           productInfo.querySelector('script[id*="ProductJson"]') ||
                           this.modalContent.querySelector('script[type="application/json"]');
        
        if (variantScript) {
          try {
            const data = JSON.parse(variantScript.textContent);
            const variants = data.variants || data;
            const variant = variants.find(v => v.id == variantId);
            if (variant) {
              console.log('Found variant data:', variant); // Debug log
              return variant;
            }
          } catch (e) {
            console.log('Error parsing variant script:', e);
          }
        }

        // Method 2: Check for variant data in data attributes
        const variantDataElement = productInfo.querySelector('[data-variants]');
        if (variantDataElement) {
          try {
            const variants = JSON.parse(variantDataElement.dataset.variants);
            const variant = variants.find(v => v.id == variantId);
            if (variant) {
              console.log('Found variant data in attribute:', variant); // Debug log
              return variant;
            }
          } catch (e) {
            console.log('Error parsing variant attribute:', e);
          }
        }

        // Method 3: Try to get from the currently selected variant data
        const selectedVariantElement = productInfo.querySelector('[data-selected-variant]');
        if (selectedVariantElement) {
          try {
            const variant = JSON.parse(selectedVariantElement.textContent);
            if (variant.id == variantId) {
              console.log('Found selected variant data:', variant); // Debug log
              return variant;
            }
          } catch (e) {
            console.log('Error parsing selected variant:', e);
          }
        }

        // Method 4: Try to extract from the variant input itself
        const variantInput = productInfo.querySelector(`input[value="${variantId}"]`);
        if (variantInput) {
          // Look for data attributes on the input or its parent
          const parent = variantInput.closest('[data-variant]');
          if (parent) {
            try {
              const variant = JSON.parse(parent.dataset.variant);
              console.log('Found variant data from input:', variant); // Debug log
              return variant;
            } catch (e) {
              console.log('Error parsing variant from input:', e);
            }
          }
        }

        console.log('Variant data not found for ID:', variantId); // Debug log
        return null;
      }
    }
  );
}
